// Ionic & Angular Imports
import {Component} from '@angular/core';
import {FormBuilder, Validators, FormGroup} from '@angular/forms';
import {Events, AlertController, PopoverController, Popover, ViewController, 
        NavParams, LoadingController, Loading} from 'ionic-angular';

// Service Imports
import {rsReportSvc} from '../../../../../services/report.service';
import {rsLaborSvc, Labor} from '../../../../../services/labor.service';
import {IEmployee} from '../../../../../services/employee.service';

// Component Imports
import {BasePage} from '../../../../base.component';
import {LaborPopover} from '../labor-popover/labor-popover.component';

@Component({
    templateUrl: 'labor-modal.html',
    providers: [
        rsLaborSvc
    ]
})
export class LaborModal extends BasePage
{
    // Class member variables
    private modalForm       : FormGroup;
    private editMode        : boolean;
    private initLabor       : Labor;
    private employeeList    : IEmployee[];

    constructor( 
        protected alertCtrl : AlertController,
        private navParams : NavParams, 
        private reportSvc: rsReportSvc, 
        private laborSvc: rsLaborSvc,
        private loadingCtrl : LoadingController,
        private viewCtrl : ViewController,
        private formBuilder : FormBuilder,
        private popupCtrl : PopoverController,
        private eventSystem : Events
    ){
        // Construct base class
        super( alertCtrl );

        // Retrieve modal data from NavParams           
        this.editMode = this.navParams.get("editmode");
        this.employeeList = this.navParams.get("employees");
        if( this.editMode ) { 
            this.initLabor = <Labor>this.navParams.get("labor");
        } else {
            // Initialize our activity object
            this.initLabor = <Labor>{
                id: 0,
                employee : {
                    id: 0,
                    firstname: "",
                    lastname: ""
                },
                starttime: "06:00",
                finishtime: "15:00",
                notes: ""
            };
        }

        // Build our form model structure
        this.buildFormModel();
    }

    // Class method for builidng our form model structure 
    private buildFormModel() {
        let name : string = "";
        if( this.initLabor.employee.id > 0 ) {
            name = this.initLabor.employee.firstname + " " + this.initLabor.employee.lastname;
        }
        this.modalForm = this.formBuilder.group({
            name:           [name, Validators.required],
            employeeid:     [this.initLabor.employee.id, Validators.required],
            starttime:      [this.initLabor.starttime, Validators.required ],
            finishtime:     [this.initLabor.finishtime, Validators.required ],
            notes:          [this.initLabor.notes]
        });
    }

    // Class helper method for setting a field in the form to a specified value
    private setFormField( fieldName: string, fieldValue: any ) {
        this.modalForm.controls[fieldName].setValue( fieldValue );
        this.modalForm.controls[fieldName].markAsDirty();
    }

    public selectEmployee(event) {
        // Create our popover instance
        let popover : Popover = this.popupCtrl.create( LaborPopover, {
            employeeList: this.employeeList
        }, {
            cssClass: "customPopover"
        } );
        
        // Handle popover callback
        popover.onWillDismiss( (data) => {
            // If there was a selection made
            if( data !== null && data.selected !== undefined ) {
                let employee : IEmployee = data.selected;
                this.setFormField( 'employeeid', employee.id );
                this.setFormField( 'name', employee.firstname + " " + employee.lastname );
            }
        });    

        // Present our popover component 
        popover.present({
            ev: event
        });    
    }    

    // Class method to close the modal view 
    public modalClose() {
        this.viewCtrl.dismiss();
    }

    public modalReset() {
        let name : string = "";
        if( this.initLabor.employee.id > 0 ) {
            name = this.initLabor.employee.firstname + " " + this.initLabor.employee.lastname;
        }        
        this.modalForm.reset( {
            name:           name,
            employeeid:     this.initLabor.employee.id,
            starttime:      this.initLabor.starttime,  
            finishtime:     this.initLabor.finishtime,
            notes:          this.initLabor.notes              
        });
    }

    public modalSubmit( formData : any, isValid : boolean ) {      
        // Display a loader while the modal submits our changes
        let loader : Loading = this.loadingCtrl.create({
            content: this.editMode ? 'Updating...' : 'Adding ...',
            dismissOnPageChange: true
        });
        loader.present().then( () => {
            // Determine what the submit button should be doing based on the validity
            // of the form and the mode of the modal.
            if( isValid === true && !this.editMode) {
                // The submit button should save a new activity
                this.laborSvc.addLabor( formData.employeeid, formData.starttime, formData.finishtime, formData.notes, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('labor:added', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error adding this laborer to the report.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                });
            } else if( isValid === true && this.editMode ) {
                // The submit button should save changes to an existing activity
                this.laborSvc.updateLabor( this.initLabor.id, formData.employeeid, formData.starttime, formData.finishtime, formData.notes, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('labor:updated', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error updating this laborer.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                }); 
            }
        });
    }

    public modalDelete() {
        // Verify that we are in the correct editMode and that our id is valid
        if( this.editMode && this.initLabor.id > 0 )
        {
            // Activity id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Laborer', 'Are you sure you want to delete "' + this.initLabor.employee.firstname + ' ' + this.initLabor.employee.lastname + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.laborSvc.deleteLabor( this.initLabor.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('labor:deleted', this.initLabor );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this laborer.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        this.viewCtrl.dismiss().then( () => {
                            loader.dismiss();
                        });
                    });
                });
            });
        } else {
            // The activity id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }    
}    


// WEBPACK FOOTER //
// ./src/pages/report/tab-main/labor/labor-modal/labor-modal.component.ts